import { useLayoutEffect, useState, type RefObject } from "react";

export type MenuSide = "up" | "down";

interface AnchoredMenuPosition {
  top: number;
  left: number;
  /** Only set when `matchAnchorWidth` is enabled; otherwise the menu keeps its own width. */
  width?: number;
}

interface UseAnchoredMenuPositionOptions {
  /** Element the menu is positioned against (e.g. the chat composer). */
  anchorElement: HTMLElement | null;
  /** Ref to the floating menu element — measured to decide flipping/clamping. */
  menuRef: RefObject<HTMLElement | null>;
  /** Only computes while open; returns the last position otherwise. */
  open: boolean;
  /**
   * Side the menu opens on by default. The menu only flips to the opposite
   * side when the preferred side can't fit the menu but the other side can.
   */
  preferredSide?: MenuSide;
  /** Gap between the anchor and the menu, in px. */
  offset?: number;
  /** Minimum gap kept from the viewport edges, in px. */
  viewportPadding?: number;
  /**
   * When true, the returned `width` matches the anchor's width (clamped to at
   * least `minWidth`), like a select dropdown. When false the menu keeps its
   * own CSS width.
   */
  matchAnchorWidth?: boolean;
  /** Floor for `width` when `matchAnchorWidth` is enabled. */
  minWidth?: number;
}

/**
 * Computes a fixed-position `{ top, left }` for a portal-rendered menu anchored
 * to an element, with collision handling so the menu never lands off-screen:
 *
 * - Opens on `preferredSide`, but flips to the opposite side when the preferred
 *   side lacks room for the menu's actual height and the other side has it.
 * - Measures the real rendered menu (height and width) rather than assuming a
 *   fixed size, so it stays correct as the list filters or grows.
 * - Clamps both axes to the viewport so a long list or a small window can't
 *   push the menu past an edge.
 * - Recomputes on scroll, resize, and whenever the anchor or menu changes size.
 *
 * Runs in a layout effect so the menu is positioned before paint (no flash from
 * an initial 0,0 placement).
 */
export function useAnchoredMenuPosition({
  anchorElement,
  menuRef,
  open,
  preferredSide = "up",
  offset = 8,
  viewportPadding = 8,
  matchAnchorWidth = false,
  minWidth = 0,
}: UseAnchoredMenuPositionOptions): AnchoredMenuPosition {
  const [position, setPosition] = useState<AnchoredMenuPosition>({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open || !anchorElement) {
      return;
    }

    const update = () => {
      const menu = menuRef.current;
      if (!menu) {
        return;
      }

      const anchor = anchorElement.getBoundingClientRect();
      const menuHeight = menu.offsetHeight;
      const width = matchAnchorWidth ? Math.max(anchor.width, minWidth) : undefined;
      const menuWidth = width ?? menu.offsetWidth;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const roomBelow = viewportHeight - anchor.bottom - offset - viewportPadding;
      const roomAbove = anchor.top - offset - viewportPadding;

      // Honour the preferred side; only flip when it can't fit but the other can.
      let side = preferredSide;
      if (preferredSide === "down" && menuHeight > roomBelow && menuHeight <= roomAbove) {
        side = "up";
      } else if (preferredSide === "up" && menuHeight > roomAbove && menuHeight <= roomBelow) {
        side = "down";
      }

      const rawTop = side === "down" ? anchor.bottom + offset : anchor.top - offset - menuHeight;

      // Clamp to the viewport so the menu is never pushed off an edge.
      const top = Math.max(
        viewportPadding,
        Math.min(rawTop, viewportHeight - menuHeight - viewportPadding),
      );
      const left = Math.max(
        viewportPadding,
        Math.min(anchor.left, viewportWidth - menuWidth - viewportPadding),
      );

      setPosition({ top, left, width });
    };

    update();

    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);

    // Reposition when the anchor or the menu (e.g. list filtered) changes size.
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(anchorElement);
    if (menuRef.current) {
      resizeObserver.observe(menuRef.current);
    }

    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
      resizeObserver.disconnect();
    };
  }, [open, anchorElement, menuRef, preferredSide, offset, viewportPadding, matchAnchorWidth, minWidth]);

  return position;
}
